import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { Transformable } from "../../../transforms/transformable.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export interface InfiniteLivesOverlayParams
{
  duration_minutes?: number;
  on_claim?: ( duration_minutes: number ) => void;
}

export class InfiniteLivesOverlay extends AbstractOverlay
{
  private static readonly DESIGN_HEIGHT = 1920;
  private static readonly BASE_DELAY_MS = 20;
  private static readonly ITEM_ANIM_DURATION_MS = 220;

  private readonly duration_minutes: number;
  private readonly on_claim?: ( duration_minutes: number ) => void;
  private claimed = false;
  private glow_controller: IController | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: InfiniteLivesOverlayParams | undefined,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.duration_minutes = Math.max( 1, Math.round( params?.duration_minutes ?? 15 ) );
    this.on_claim = params?.on_claim;
    this.createElements();
  }

  private createElements(): void
  {
    void audioController.playSound( AudioClips.BOOSTER_APPEAR );

    const base_now = performance.now() + InfiniteLivesOverlay.BASE_DELAY_MS;
    const addAppearTransform = (
      element: Transformable,
      start_time: number,
      initial_scale?: number
    ): void =>
    {
      element.setOpacity( 0 );
      element.addTransform( new TransformOpacity(
        element,
        true,
        0,
        1,
        start_time,
        InfiniteLivesOverlay.ITEM_ANIM_DURATION_MS,
        { easing: Easing.EaseOutQuad }
      ) );

      if ( initial_scale === undefined ) return;

      element.setScale( new Vector2( initial_scale, initial_scale ) );
      element.addTransform( new TransformScale(
        element,
        true,
        null,
        new Vector2( 1, 1 ),
        start_time,
        InfiniteLivesOverlay.ITEM_ANIM_DURATION_MS,
        { easing: Easing.EaseOutQuad }
      ) );
    };

    this.createTitle( base_now, addAppearTransform );
    this.createHeart( base_now + 80, addAppearTransform );
    this.createDuration( base_now + 140, addAppearTransform );
    this.createClaimButton();
  }

  private createTitle(
    start_time: number,
    addAppearTransform: ( element: Transformable, start_time: number, initial_scale?: number ) => void
  ): void
  {
    const title_w_design = 640;
    const title_h_design = 236;
    const title_center_y_design = 450;

    const title_w = LayoutScaler.widthFromDesign( title_w_design );
    const title_h = LayoutScaler.heightFromDesign( title_h_design );
    const title_x = this.rect.getW2() - title_w / 2;
    const title_y = this.yFromDesignCenter( title_center_y_design, title_h );

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "INFINITE_HEART.DESCRIPTION",
        font_size: LayoutScaler.textSizeFromDesign( 100 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: "#E58900",
        auto_wrap: true,
        fit_font_size: false,
        line_height: 1,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    addAppearTransform( title, start_time, 0.85 );
    this.addChild( title );
  }

  private createHeart(
    start_time: number,
    addAppearTransform: ( element: Transformable, start_time: number, initial_scale?: number ) => void
  ): void
  {
    const heart_size_design = 252;
    const heart_center_y_design = 960;

    const heart_size = LayoutScaler.widthFromDesign( heart_size_design );
    const heart_x = this.rect.getW2() - heart_size / 2;
    const heart_y = this.yFromDesignCenter( heart_center_y_design, heart_size );

    const heart = new UIImage(
      this.ctx,
      heart_x,
      heart_y,
      heart_size,
      heart_size,
      ImgID.UI_ShopInfiniteHeart
    );

    addAppearTransform( heart, start_time, 0.55 );
    this.addChild( heart );

    const heart_absolute = heart.getAbsolutePosition();
    this.glow_controller = Vfx2DManager.addCoinCounterGlow(
      new Vector2(
        heart_absolute.x + heart_size / 2,
        heart_absolute.y + heart_size / 2
      ),
      LayoutScaler.widthFromDesign( 160 ),
      performance.now(),
      1000,
      true,
      180,
      true,
      0.42
    );
  }

  private createDuration(
    start_time: number,
    addAppearTransform: ( element: Transformable, start_time: number, initial_scale?: number ) => void
  ): void
  {
    const duration_w_design = 198;
    const duration_h_design = 123;
    const duration_center_y_design = 1085.5;
    const duration_font_size_design = 90;

    const duration_w = LayoutScaler.widthFromDesign( duration_w_design );
    const duration_h = LayoutScaler.heightFromDesign( duration_h_design );
    const duration_x = this.rect.getW2() - duration_w / 2;
    const duration_y = this.yFromDesignCenter( duration_center_y_design, duration_h );

    const duration = new UITextElement(
      this.ctx,
      duration_x,
      duration_y,
      duration_w,
      duration_h,
      {
        localization_key: "N_MINUTES",
        text_variable_params: [ String( this.duration_minutes ) ],
        font_size: LayoutScaler.textSizeFromDesign( duration_font_size_design ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        line_height: duration_h_design / duration_font_size_design,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    addAppearTransform( duration, start_time );
    this.addChild( duration );
  }

  private createClaimButton(): void
  {
    const button_w_design = 700;
    const button_h_design = 280;
    const button_center_y_design = 1515;

    const button_w = LayoutScaler.widthFromDesign( button_w_design );
    const button_h = LayoutScaler.heightFromDesign( button_h_design );
    const button_x = this.rect.getW2() - button_w / 2;
    const button_y = this.yFromDesignCenter( button_center_y_design, button_h );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    const button_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 8 ),
      button_w,
      button_h,
      {
        localization_key: "GET",
        font_size: LayoutScaler.textSizeFromDesign( 72 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.menu_button_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );

    button.addChild( button_text, true );
    button.setOnClick( () =>
    {
      this.claimed = true;
      OverlayManager.closeOverlay();
    } );
    this.addChild( button );
  }

  private yFromDesignCenter( design_center_y: number, element_height: number ): number
  {
    return this.rect.getH() * design_center_y / InfiniteLivesOverlay.DESIGN_HEIGHT
      - element_height / 2;
  }

  public override onOverlayWillClose(): void
  {
    this.glow_controller?.stop();
    this.glow_controller = null;

    if ( this.claimed )
      this.on_claim?.( this.duration_minutes );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "InfiniteLivesOverlay";
  }
}
